import { memo, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react"
import type { PlaybackTimeStore } from "@/lib/timeline"
import { cn } from "@/lib/utils"

export const MIN_PX_PER_MS = 0.005
export const MAX_PX_PER_MS = 1

/**
 * Fit-to-width: сколько пикселей на миллисекунду, чтобы линейка влезла в дорожку.
 * Запас ~2% — без горизонтального скролла на дробях ширины.
 */
export function fitTimelinePxPerMs(laneWidth: number, durationMs: number, headerW = 0): number {
  const fitDurationMs = Math.max(durationMs, 1) * 1.02
  const available = Math.max(laneWidth - headerW, 1)
  return Math.min(MAX_PX_PER_MS, Math.max(MIN_PX_PER_MS, available / fitDurationMs))
}

const RULER_STEPS_S = [0.1, 0.2, 0.5, 1, 2, 5, 10, 30, 60, 120, 300, 600]
const RULER_MINOR_DIVISIONS = [10, 5, 4, 2]
const RULER_MAJOR_MIN_PX = 50
const RULER_MINOR_MIN_PX = 8

export function formatTimecode(ms: number, fps: number): string {
  const safeMs = Math.max(0, ms)
  const totalSec = Math.floor(safeMs / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const f = Math.floor(((safeMs % 1000) / 1000) * fps)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(h)}:${pad(m)}:${pad(s)}:${pad(f)}`
}

export function rulerTickPlan(pxPerMs: number): { majorStepS: number; divisions: number; minorStepMs: number } {
  const majorStepS =
    RULER_STEPS_S.find((s) => s * 1000 * pxPerMs >= RULER_MAJOR_MIN_PX) ?? RULER_STEPS_S[RULER_STEPS_S.length - 1]
  const majorSpacingPx = majorStepS * 1000 * pxPerMs
  const divisions = RULER_MINOR_DIVISIONS.find((d) => majorSpacingPx / d >= RULER_MINOR_MIN_PX) ?? 1
  return { majorStepS, divisions, minorStepMs: (majorStepS * 1000) / divisions }
}

export function formatRulerLabel(seconds: number): string {
  const rounded = Math.round(seconds * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1)
}

export function TimecodeDisplay({
  store,
  fps,
  durationMs,
  title,
}: {
  store: PlaybackTimeStore
  fps: number
  durationMs: number
  title: string
}) {
  const currentRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    let lastText = ""
    const update = () => {
      const text = formatTimecode(store.get(), fps)
      if (text === lastText) return
      lastText = text
      if (currentRef.current) currentRef.current.textContent = text
    }
    update()
    return store.subscribe(update)
  }, [store, fps])

  return (
    <span className="font-mono text-[11px] text-ui-text-secondary tabular-nums" title={title}>
      <span ref={currentRef} />
      <span className="mx-1 text-ui-text-muted">/</span>
      {formatTimecode(durationMs, fps)}
    </span>
  )
}

export const Playhead = memo(function Playhead({
  store,
  pxPerMs,
  offsetPx,
  playingRef,
  scrollRef,
  scrollWithPlayhead = true,
}: {
  store: PlaybackTimeStore
  pxPerMs: number
  offsetPx: number
  playingRef: RefObject<boolean>
  scrollRef: RefObject<HTMLDivElement | null>
  /** false — линейка вписана в плашку, сдвиг scrollLeft во время play не нужен. */
  scrollWithPlayhead?: boolean
}) {
  const lineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const update = () => {
      const x = offsetPx + store.get() * pxPerMs
      if (lineRef.current) lineRef.current.style.transform = `translateX(${x}px)`

      if (!scrollWithPlayhead || !playingRef.current) return
      const el = scrollRef.current
      if (!el || el.scrollWidth <= el.clientWidth) return
      const visibleLeft = el.scrollLeft + offsetPx
      const visibleRight = el.scrollLeft + el.clientWidth - 16
      if (x < visibleLeft || x > visibleRight) {
        el.scrollLeft = Math.max(0, x - offsetPx)
      }
    }
    update()
    return store.subscribe(update)
  }, [store, pxPerMs, offsetPx, playingRef, scrollRef, scrollWithPlayhead])

  return (
    <div
      ref={lineRef}
      className="pointer-events-none absolute top-0 bottom-0 left-0 z-30 w-px bg-ui-accent shadow-sm shadow-violet-400/50"
    />
  )
})

export type TimelineRulerProps = {
  pxPerMs: number
  durationMs: number
  onSeek: (ms: number) => void
  onScrubStart?: () => void
  onScrubEnd?: () => void
  className?: string
  rulerRef?: RefObject<HTMLDivElement | null>
}

export const TimelineRuler = memo(function TimelineRuler({
  pxPerMs,
  durationMs,
  onSeek,
  onScrubStart,
  onScrubEnd,
  className,
  rulerRef: rulerRefProp,
}: TimelineRulerProps) {
  const internalRef = useRef<HTMLDivElement>(null)
  const rulerRef = rulerRefProp ?? internalRef
  const scrubbingRef = useRef(false)
  const viewDurationMs = Math.max(durationMs, 1)
  const { majorStepS: rulerStepS, divisions: rulerDivisions, minorStepMs: rulerMinorMs } = rulerTickPlan(pxPerMs)

  const msFromEvent = useCallback(
    (e: React.PointerEvent) => {
      const el = rulerRef.current
      if (!el || pxPerMs <= 0) return null
      const rect = el.getBoundingClientRect()
      return Math.max(0, (e.clientX - rect.left) / pxPerMs)
    },
    [pxPerMs, rulerRef],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const ms = msFromEvent(e)
      if (ms == null) return
      scrubbingRef.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      onScrubStart?.()
      onSeek(ms)
    },
    [msFromEvent, onScrubStart, onSeek],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!scrubbingRef.current) return
      const ms = msFromEvent(e)
      if (ms == null) return
      onSeek(ms)
    },
    [msFromEvent, onSeek],
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const wasScrubbing = scrubbingRef.current
      scrubbingRef.current = false
      if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
      }
      if (wasScrubbing) onScrubEnd?.()
    },
    [onScrubEnd],
  )

  // Тиков могут быть сотни — держим массив в useMemo, иначе он пересобирается
  // на каждый рендер родителя (во время драга это было бы на каждый pointermove).
  const ticks = useMemo(() => {
    if (pxPerMs <= 0) return null
    return Array.from({ length: Math.floor(viewDurationMs / rulerMinorMs) + 1 }, (_, i) => {
      const isMajor = i % rulerDivisions === 0
      return (
        <div
          key={i}
          className="pointer-events-none absolute inset-y-0 flex items-end"
          style={{ left: i * rulerMinorMs * pxPerMs }}
        >
          <div className={isMajor ? "h-2 w-px bg-ui-border-strong" : "h-1 w-px bg-ui-border opacity-70"} />
          {isMajor ? (
            <span className="ml-0.5 pb-px text-[8px] text-ui-text-muted tabular-nums">
              {formatRulerLabel((i / rulerDivisions) * rulerStepS)}
            </span>
          ) : null}
        </div>
      )
    })
  }, [pxPerMs, viewDurationMs, rulerMinorMs, rulerDivisions, rulerStepS])

  return (
    <div
      ref={rulerRef}
      className={cn(
        "relative h-5 min-w-0 flex-1 cursor-ew-resize touch-none rounded-md bg-ui-surface-sunken/60 select-none",
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {ticks}
    </div>
  )
})

export type UseTimelineZoomOptions = {
  durationMs: number
  /** Левый отступ контента (например ширина sticky-заголовков треков). */
  headerW?: number
  /** Сброс зума при смене ключа (например sourcesKey / animatic id). */
  resetKey?: string | number | null
}

export type UseTimelineZoomResult = {
  pxPerMs: number
  lanesRef: RefObject<HTMLDivElement | null>
  /** Callback-ref на контейнер дорожек: observer не теряется, если lanes монтируются не на первом commit. */
  setLanesRef: (node: HTMLDivElement | null) => void
  laneWidth: number
  laneContentPx: number
}

/**
 * Fit-to-width + Ctrl/Cmd+wheel zoom с якорем под курсором.
 * По умолчанию без юзер-зума контент влезает без горизонтального скролла.
 */
export function useTimelineZoom({ durationMs, headerW = 0, resetKey }: UseTimelineZoomOptions): UseTimelineZoomResult {
  const lanesRef = useRef<HTMLDivElement | null>(null)
  const [lanesEl, setLanesEl] = useState<HTMLDivElement | null>(null)
  const [laneWidth, setLaneWidth] = useState(0)
  const [pxPerMs, setPxPerMs] = useState(0)
  const pxPerMsRef = useRef(pxPerMs)
  pxPerMsRef.current = pxPerMs
  const pendingZoomRef = useRef<{ anchorMs: number; viewportX: number } | null>(null)
  const userZoomedRef = useRef(false)

  const setLanesRef = useCallback((node: HTMLDivElement | null) => {
    lanesRef.current = node
    setLanesEl(node)
  }, [])

  useEffect(() => {
    if (!lanesEl) {
      setLaneWidth(0)
      return
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0
      setLaneWidth(w)
    })
    ro.observe(lanesEl)
    setLaneWidth(lanesEl.clientWidth)
    return () => ro.disconnect()
  }, [lanesEl])

  // Смена источника (upload → web_preview, новый signed URL) не меняет ширину
  // контейнера. Fit обязан пересчитаться по resetKey, иначе pxPerMs остаётся 0
  // после старого setPxPerMs(0) — клип схлопывается в полоску 6px до refresh.
  useEffect(() => {
    userZoomedRef.current = false
  }, [resetKey])

  const rulerDurationMs = Math.max(durationMs, 1)

  useEffect(() => {
    if (laneWidth <= 0) return
    if (userZoomedRef.current) return
    setPxPerMs(fitTimelinePxPerMs(laneWidth, rulerDurationMs, headerW))
  }, [laneWidth, rulerDurationMs, headerW, resetKey])

  useEffect(() => {
    if (!lanesEl) return
    const onWheel = (e: WheelEvent) => {
      if (!e.metaKey && !e.ctrlKey) return
      e.preventDefault()
      const current = pxPerMsRef.current
      if (current <= 0) return
      const rect = lanesEl.getBoundingClientRect()
      const viewportX = e.clientX - rect.left
      const anchorMs = (lanesEl.scrollLeft + viewportX - headerW) / current
      const next = Math.min(MAX_PX_PER_MS, Math.max(MIN_PX_PER_MS, current * Math.exp(-e.deltaY * 0.002)))
      if (next === current) return
      userZoomedRef.current = true
      pendingZoomRef.current = { anchorMs, viewportX }
      setPxPerMs(next)
    }
    lanesEl.addEventListener("wheel", onWheel, { passive: false })
    return () => lanesEl.removeEventListener("wheel", onWheel)
  }, [headerW, lanesEl])

  useEffect(() => {
    const el = lanesRef.current
    const pending = pendingZoomRef.current
    if (!el || !pending || pxPerMs <= 0) return
    el.scrollLeft = Math.max(0, pending.anchorMs * pxPerMs + headerW - pending.viewportX)
    pendingZoomRef.current = null
  }, [pxPerMs, headerW])

  const laneContentPx = Math.max(rulerDurationMs * pxPerMs, laneWidth - headerW)

  return { pxPerMs, lanesRef, setLanesRef, laneWidth, laneContentPx }
}
